import { runWithErrorReporting } from "$root/api/error-reporting";
import { useApiGen } from "$root/api/hooks";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { unitOfMeasurement } from "$root/functional-areas/metrics";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { portfolioWidgetMissingDataReason, WidgetStatus } from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	type ContextContent,
	arrayToBoolean,
	downloadContentDisposition,
	qualifier,
	ToastableError,
	withContext,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { type PortfolioMetric, PortfolioStudioPreferencesApiFactory } from "@eu.mdotm.api/sphere";
import { type OrderBy, Row, Text } from "@mdotm/shadcn/legacy/components";
import { DropdownMenu } from "@mdotm/shadcn/mdotm/components";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { Map } from "immutable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	type PortfolioMetricsRow,
	PortfolioMetricsBase,
	ReapplySphereMetricsIconTooltip,
} from "./PortfolioMetricsBase";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { buildWidgetExportConfig } from "../widget-export";

const PerformanceMetricsBlock = (props: ContextContent<typeof PortfolioContext>) => {
	const uuid = props.portfolio?.uuid;
	const enhanced = props.enhanced;
	const benchmarkId = props.selectedBenchmark;
	const { asOf, asOfSnapshot } = props;
	const { t } = useTranslation();
	const portfolioStudioPreferences = useApiGen(PortfolioStudioPreferencesApiFactory);

	// Mirror the table's column sort so the export reproduces it. Defaults to none (the data provider's
	// natural metric order); once the user sorts a column the export carries that same sort.
	const [orderBy, setOrderBy] = useState<OrderBy[]>([]);

	const isCalculating = useMemo(
		() =>
			(props.portfolio?.action === "UPLOAD" || props.portfolio?.action === "CREATION") &&
			props.portfolio?.status === "CALCULATING",
		[props.portfolio?.action, props.portfolio?.status],
	);

	// Download — mirrors the visible table via the smart-workspace `investmentPortfolioMetric` data provider.
	const exportPortfolioMetrics = async (investmentUuid: string) => {
		const config = buildWidgetExportConfig(
			"PerformanceMetricsBlock",
			{
				investmentUuid,
				source: enhanced ? "PROPOSAL" : "CURRENT",
				sort: orderBy,
			},
			{ asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export portfolio metrics",
					payload: JSON.stringify(config),
				},
			},
		);
	};

	const exportPortfolioMetricsRef = useUnsafeUpdatedRef(exportPortfolioMetrics);

	const downloadActionHeader = useMemo(
		() =>
			uuid ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioPerformanceMetrics.downloadButton }}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportPortfolioMetricsRef.current(uuid);
							},
							label: t("PERFORMANCE_METRICS.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioPerformanceMetrics.downloadItem,
						},
					]}
				/>
			) : null,
		[uuid, t, exportPortfolioMetricsRef],
	);

	return (
		<PortfolioMetricsBase
			proposalDate={props.portfolio.modificationDate}
			hasBenchmark={Boolean(benchmarkId)}
			actionHeader={downloadActionHeader}
			orderBy={orderBy}
			onOrderByChange={setOrderBy}
			title={({ lastImportDate, hasCustomMetrics }) => (
				<Row alignItems="center" gap={8}>
					<Text
						type="Body/XL/Bold"
						title={t("PERFORMANCE_METRICS.TITLE")}
						className="truncate"
						data-qualifier={qualifier.widgets.portfolioPerformanceMetrics.name}
					>
						{t("PERFORMANCE_METRICS.TITLE")}
					</Text>
					<InfoTooltip>{t("portfolio.metrics.tooltip")}</InfoTooltip>
					{lastImportDate && hasCustomMetrics && (
						<ReapplySphereMetricsIconTooltip lastUpdate={lastImportDate} portfolio={props.portfolio} />
					)}
				</Row>
			)}
			noDataText={t("PERFORMANCE_METRICS.NO_DATA")}
			enabled={isAsOfReadable(asOf, asOfSnapshot)}
			queryKey={[...portfolioRepository.metrics.fullQueryKey, props.portfolio?.uuid, benchmarkId, asOf]}
			saveHandler={(preferences) =>
				runWithErrorReporting(
					async () => {
						const somePreferencesAreEnabled = preferences.some((p) => p.visible);
						if (!somePreferencesAreEnabled) {
							throw new ToastableError(
								"Oops! You need at least one visible column. Please enable at least one metrics to continue",
								{
									icon: "Portfolio",
								},
							);
						}

						await axiosExtract(
							portfolioStudioPreferences.setPortfolioMetricsOrderingPreferences({
								portfolioMetricPreferences: preferences.map((x) => ({
									enabled: x.visible,
									metricType: x.type!,
									metricParameters: { classificationUuid: x.classificationUuid },
								})),
							}),
						);
					},
					{
						area: "portfolio",
						attemptedOperation: {
							message: "update portfolio metrics preferences",
						},
					},
				)
			}
			metricsProvider={async () => {
				const preferences = await axiosExtract(portfolioStudioPreferences.getPortfolioMetricsOrderingPreferences());
				const metrics = await portfolioRepository.metrics.fetchQuery(props.portfolio?.uuid ?? "", {
					benchmarkId,
					preferences,
					asOf,
				});

				if (isCalculating || !arrayToBoolean(preferences.portfolioMetricPreferences)) {
					return {
						data: undefined,
						widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "PerformanceMetricsBlock"),
					};
				}

				const somePreferenceAreEnabled = preferences.portfolioMetricPreferences?.some((x) => x.enabled);
				if (
					somePreferenceAreEnabled &&
					!arrayToBoolean(metrics?.portfolioMetrics) &&
					!arrayToBoolean(metrics?.customPortfolioMetrics)
				) {
					return {
						data: undefined,
						// A snapshot that came back without metrics for a past date will never gain them, so the live
						// reasons — which promise the calculation is on its way — give way to EMPTY, the status the
						// widget bases turn into the date-specific wall (MDOT-24860).
						widgetStatus: asOf
							? WidgetStatus.EMPTY
							: portfolioWidgetMissingDataReason(props.portfolio, "PerformanceMetricsBlock"),
					};
				}

				const metricsMap = Map<string, PortfolioMetric>(
					metrics?.portfolioMetrics?.flatMap((metric) => (metric.type ? [[metric.type, metric]] : [])),
				);

				const customMetricsMap = Map<string, PortfolioMetric>(
					metrics?.customPortfolioMetrics?.flatMap((metric) => (metric.type ? [[metric.type, metric]] : [])),
				);

				const provider = preferences.portfolioMetricPreferences?.flatMap((preference): PortfolioMetricsRow[] => {
					if (!preference.metricType) {
						return [];
					}

					if (preference.metricType === "SCORE_AVERAGE_NAN_AS_ZEROS") {
						return [];
					}
					const key = preference.metricType;
					const label = preference.metadata?.classificationName
						? `Avg ${preference.metadata.classificationName}`
						: (t(`PERFORMANCE_METRICS.METRICS.FULL.${preference.metricType}`) ?? "-");

					return [
						{
							label,
							name: label,
							type: preference.metricType,
							classificationUuid: preference.metricParameters?.classificationUuid,
							visible: preference.enabled ?? false,
							current: customMetricsMap.get(key)?.value ?? metricsMap.get(key)?.value,
							benchmark: customMetricsMap.get(key)?.benchmarkValue ?? metricsMap.get(key)?.benchmarkValue,
							isCurrentCustom: customMetricsMap.get(key)?.value != null,
							isBenchmarkCustom: customMetricsMap.get(key)?.benchmarkValue != null,
							metricParameters: preference.metricParameters,
							unitOfMeasurement: unitOfMeasurement.portfolioMetric[preference.metricType] ?? "",
						},
					];
				});

				return {
					data: {
						metrics: provider ?? [],
						lastImportDate: metrics?.metadataUpdate ?? "",
						hasCustomMetrics: customMetricsMap.size > 0,
					},
					widgetStatus: WidgetStatus.READY as const,
				};
			}}
		/>
	);
};

export default withContext(PortfolioContext)(PerformanceMetricsBlock);
